import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import { useOverrideTimeOffRequest } from '../../hooks/useOverrideTimeOffRequest';

interface OverrideDialogProps {
  requestId: string;
}

/** Owner/Admin only — bypasses the approval chain entirely. `reason` is required. */
export function OverrideDialog({ requestId }: OverrideDialogProps) {
  const { t } = useTranslation('timeOff');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const overrideRequest = useOverrideTimeOffRequest();

  function handleDecision(approve: boolean) {
    overrideRequest.mutate(
      { requestId, payload: { approve, reason } },
      {
        onSuccess: () => {
          setOpen(false);
          setReason('');
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="time-off-override-trigger">
          <ShieldAlert className="size-4" />
          {t('approvals.override.title')}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="time-off-override-dialog">
        <DialogHeader>
          <DialogTitle>{t('approvals.override.title')}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('approvals.override.reason')}
          data-testid="time-off-override-reason"
        />
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() => handleDecision(false)}
            disabled={!reason || overrideRequest.isPending}
            data-testid="time-off-override-reject"
          >
            {t('approvals.override.reject')}
          </Button>
          <Button
            onClick={() => handleDecision(true)}
            disabled={!reason || overrideRequest.isPending}
            data-testid="time-off-override-approve"
          >
            {t('approvals.override.approve')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
